import { FC } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { Userbot } from "../../interfaces"
import { formatPhone, shortId } from "./identityFormat"
import { IdentityTooltip } from "./identityTooltip"
import { AccountIdentity } from "./accountIdentity"
import { useAccountsLookup } from "../../hooks"

export interface ClientIdentityProps {
  client: Userbot.Client | Userbot.FeedClient | null | undefined
  accountOverride?: Userbot.Account | Userbot.AccountFeedItem | null
  pendingLogin?: boolean
  compact?: boolean
  showAccount?: boolean
  showTooltip?: boolean
  proxyLabel?: string | null
}

export const ClientIdentity: FC<ClientIdentityProps> = ({
  client,
  accountOverride,
  pendingLogin = false,
  compact = false,
  showAccount = true,
  showTooltip = true,
  proxyLabel,
}) => {
  const { getAccount } = useAccountsLookup()

  if (!client) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const isPending =
    pendingLogin ||
    accountOverride?.id === null ||
    (accountOverride as Userbot.AccountFeedItem)?.pendingLogin === true

  const rawAccountId =
    "accountId" in client ? (client as Userbot.Client).accountId : undefined

  const resolvedAccount =
    accountOverride !== undefined
      ? accountOverride
      : rawAccountId
        ? getAccount(rawAccountId)
        : null

  if (compact) {
    let accountSuffix = ""
    if (isPending) {
      accountSuffix = "Ожидает входа"
    } else if (resolvedAccount) {
      accountSuffix =
        resolvedAccount.telegramUserId ||
        formatPhone(resolvedAccount.phone) ||
        shortId(resolvedAccount.id)
    } else if (rawAccountId) {
      accountSuffix = shortId(rawAccountId)
    }

    const label = accountSuffix
      ? `${client.name} · ${accountSuffix}`
      : client.name

    const element = (
      <Typography
        variant="body2"
        color={isPending ? "warning.main" : "text.primary"}
        noWrap
        sx={{
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    )

    return showTooltip ? (
      <IdentityTooltip
        client={client}
        account={resolvedAccount}
        proxyLabel={proxyLabel}
      >
        {element}
      </IdentityTooltip>
    ) : (
      element
    )
  }

  const content = (
    <Stack spacing={0} sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={500} noWrap>
        {client.name}
      </Typography>
      {isPending ? (
        <Typography variant="caption" color="warning.main" noWrap>
          Ожидает входа
        </Typography>
      ) : showAccount ? (
        resolvedAccount ? (
          <AccountIdentity
            account={resolvedAccount}
            compact
            showTooltip={false}
          />
        ) : rawAccountId ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
            noWrap
          >
            {shortId(rawAccountId)}
          </Typography>
        ) : null
      ) : null}
    </Stack>
  )

  return showTooltip ? (
    <IdentityTooltip
      client={client}
      account={resolvedAccount}
      proxyLabel={proxyLabel}
    >
      <Box sx={{ display: "inline-block", minWidth: 0 }}>{content}</Box>
    </IdentityTooltip>
  ) : (
    content
  )
}
